/* eslint-disable react/jsx-max-props-per-line */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, IconButton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import { ExpandableTableV2, WidgetV2 } from '../../../../components/v2';
import TableService from '../../../../services/TableService';
import {
  formatHistoricoDiaFilter,
  formatPeriodoCurto,
  getHistoricoVolumeUnit,
  HISTORICO_NSMP_FILTERS,
  HISTORICO_SORT_MODES,
  HISTORICO_SORT_OPTIONS,
  historicoSortToApi,
  sortHistoricoRows,
} from './constants';
import DigitacaoSortMenuButton from './DigitacaoSortMenuButton';
import HistoricoDigitacaoStatusCell from './HistoricoDigitacaoStatusCell';
import HistoricoNsmpDetail from './HistoricoNsmpDetail';
import {
  renderMalhaPoloAlimentador,
  renderNsirCodigoEquipamento,
  renderStatusComDataDigitacao,
  renderTipoServicoSubtipo,
} from './historicoTableCells';

const HistoricoPeriodoTable = ({
  apiFilter,
  refreshKey,
  dataDigitacaoFilter,
  inicio,
  fim,
  lenteId,
  lente,
  paginationUrl,
  lenteLabel,
  emptyMessage,
  onClearDayFilter,
}) => {
  const volumeUnit = getHistoricoVolumeUnit(lente ?? { id: lenteId });
  const resolvedEmptyMessage = emptyMessage ?? (
    lenteLabel
      ? `Nenhuma digitação ${lenteLabel} neste período.`
      : 'Nada neste período — serviços digitados no intervalo aparecem aqui.'
  );
  const nsmpCacheRef = useRef(new Map());
  const [expanded, setExpanded] = useState({});
  const [subtableFilterByNsir, setSubtableFilterByNsir] = useState({});
  const [sortMode, setSortMode] = useState(HISTORICO_SORT_MODES.PENDENTES_FIRST);

  const filterData = useMemo(() => {
    const base = {
      ...apiFilter,
      ...historicoSortToApi(sortMode),
    };
    if (dataDigitacaoFilter) {
      base.dataDigitacao = dataDigitacaoFilter;
    }
    return base;
  }, [apiFilter, dataDigitacaoFilter, sortMode]);

  useEffect(() => {
    nsmpCacheRef.current.clear();
    setExpanded({});
    setSubtableFilterByNsir({});
  }, [refreshKey, inicio, fim, dataDigitacaoFilter, lenteId, sortMode]);

  const handleForaPeriodoClick = useCallback((row) => {
    const rowId = String(row.nsirId ?? row.id);
    setSubtableFilterByNsir((prev) => ({
      ...prev,
      [rowId]: HISTORICO_NSMP_FILTERS.FORA_PERIODO,
    }));
    setExpanded({ [rowId]: true });
  }, []);

  const fetchHistorico = useCallback(async (page, pageSize, filter) => {
    const response = await TableService.request(
      `reports/digitacao/me/historico?p=${page}&s=${pageSize}`,
      filter,
    );
    const rows = response?.data?.rows || [];
    return {
      rows: sortHistoricoRows(rows, sortMode),
      totalRows: response?.data?.totalRows ?? 0,
    };
  }, [sortMode]);

  const columns = useMemo(
    () => [
      {
        id: 'expand',
        header: '',
        size: 48,
        cell: ({ row }) =>
          row.getCanExpand() ? (
            <IconButton
              size="small"
              aria-label={row.getIsExpanded() ? 'Recolher serviços' : 'Expandir serviços'}
              aria-expanded={row.getIsExpanded()}
              onClick={row.getToggleExpandedHandler()}
            >
              {row.getIsExpanded() ? (
                <ExpandMoreIcon fontSize="small" />
              ) : (
                <ChevronRightIcon fontSize="small" />
              )}
            </IconButton>
          ) : null,
      },
      {
        id: 'nsirCodigo',
        header: 'NSIR',
        size: 160,
        minSize: 140,
        cell: ({ row }) => renderNsirCodigoEquipamento(row.original),
      },
      {
        id: 'tipoServico',
        header: 'Tipo Serviço',
        size: 160,
        minSize: 140,
        cell: ({ row }) => renderTipoServicoSubtipo(row.original),
      },
      {
        id: 'localRede',
        header: 'Local',
        size: 200,
        minSize: 168,
        cell: ({ row }) => renderMalhaPoloAlimentador(row.original),
      },
      {
        id: 'nsirStatus',
        header: 'Status',
        size: 168,
        minSize: 148,
        cell: ({ row }) => renderStatusComDataDigitacao(row.original),
      },
      {
        id: 'volumesDigitacao',
        header: volumeUnit === 'postes' ? 'Postes digitados' : 'Status da Digitação',
        size: 150,
        minSize: 200,
        cell: ({ row }) => (
          <HistoricoDigitacaoStatusCell
            row={row.original}
            onForaPeriodoClick={handleForaPeriodoClick}
            volumeUnit={volumeUnit}
          />
        ),
      },
    ],
    [handleForaPeriodoClick, volumeUnit],
  );

  const periodoLabel = formatPeriodoCurto(inicio, fim);
  const diaFilterLabel = formatHistoricoDiaFilter(dataDigitacaoFilter);
  const lenteSuffix = lenteLabel ? ` · ${lenteLabel}` : '';
  const subheader = periodoLabel
    ? `NSIRs com digitação no período · ${periodoLabel}${lenteSuffix} · expandir mostra todos os serviços`
    : `NSIRs com digitação no período${lenteSuffix} · expandir mostra todos os serviços`;

  const getRowId = useCallback((row) => String(row.nsirId ?? row.id), []);

  return (
    <WidgetV2
      title="Histórico do período"
      subheader={subheader}
      initialExpanded
      actions={[
        {
          isComponent: true,
          component: (
            <DigitacaoSortMenuButton
              sortMode={sortMode}
              onChange={setSortMode}
              options={HISTORICO_SORT_OPTIONS}
            />
          ),
        },
      ]}
    >
      {diaFilterLabel && onClearDayFilter && (
        <Alert
          severity="info"
          sx={{ mx: 2, mt: 1, mb: 0 }}
          action={(
            <Button color="inherit" size="small" onClick={onClearDayFilter} startIcon={<CloseIcon />}>
              Limpar
            </Button>
          )}
        >
          Filtrando dia {diaFilterLabel}
        </Alert>
      )}
      <ExpandableTableV2
        useApi
        fetchData={fetchHistorico}
        paginationUrl={paginationUrl}
        filterData={filterData}
        refreshKey={refreshKey}
        rowHeight={84}
        pageSize={10}
        columns={columns}
        singleExpand
        expanded={expanded}
        onExpandedChange={setExpanded}
        getRowId={getRowId}
        getRowCanExpand={(row) => {
          const count = row.original.nsmpCount ?? row.original.totalServicosNsir ?? row.original.totalServicos;
          if (count != null) return Number(count) > 0;
          return Boolean(row.original.nsirId);
        }}
        renderSubComponent={({ row }) => {
          const rowId = getRowId(row.original);
          return (
            <HistoricoNsmpDetail
              nsirId={row.original.nsirId}
              nsirCodigo={row.original.nsirCodigo}
              inicio={inicio}
              fim={fim}
              lenteId={lenteId ?? lente?.id}
              cacheRef={nsmpCacheRef}
              initialFilter={subtableFilterByNsir[rowId] ?? HISTORICO_NSMP_FILTERS.TODOS}
            />
          );
        }}
        emptyMessage={resolvedEmptyMessage}
      />
    </WidgetV2>
  );
};

HistoricoPeriodoTable.propTypes = {
  apiFilter: PropTypes.object.isRequired,
  refreshKey: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  dataDigitacaoFilter: PropTypes.number,
  inicio: PropTypes.number,
  fim: PropTypes.number,
  lenteId: PropTypes.string,
  lente: PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    unidade: PropTypes.string,
  }),
  paginationUrl: PropTypes.string,
  lenteLabel: PropTypes.string,
  emptyMessage: PropTypes.string,
  onClearDayFilter: PropTypes.func,
};

HistoricoPeriodoTable.defaultProps = {
  refreshKey: false,
  dataDigitacaoFilter: null,
  inicio: null,
  fim: null,
  lenteId: null,
  lente: null,
  paginationUrl: '/v2/minha-digitacao',
  lenteLabel: null,
  emptyMessage: null,
  onClearDayFilter: undefined,
};

export default HistoricoPeriodoTable;
